import { Box, ButtonBase } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { rangesEqual, type IsoDateRange } from './isoDate';
import type { DateRangePreset } from './presets';

/** Figma `dropdowns` `40245:262685`: a 205-wide column of 36-high rows beside the grid. */
const Column = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: 205,
  flexShrink: 0,
  padding: 8,
  boxSizing: 'border-box',
  overflowY: 'auto',
  backgroundColor: theme.palette.background.paper,
}));

const PresetButton = styled(ButtonBase)(({ theme }) => ({
  justifyContent: 'flex-start',
  width: '100%',
  minHeight: 36,
  flexShrink: 0,
  padding: '8px 12px',
  borderRadius: 4,
  fontFamily: theme.typography.fontFamily,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1.4,
  textAlign: 'left',
  color: theme.palette.text.secondary,
  // The frame's highlighted row (`neutrals/white/hover`) is both hover and the current one.
  '&:hover, &[aria-pressed="true"]': { backgroundColor: theme.palette.background.default },
  '&.Mui-focusVisible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
}));

const Divider = styled('hr')(({ theme }) => ({
  width: '100%',
  margin: '4px 0',
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
}));

/**
 * The column beside the grid: the "Open end" row first where the range may be left open
 * (brain §5.10, PM 2026-10-23 — offered where the dates are picked, never as a control
 * beside the field), then the caller's quick picks.
 */
export function PresetList({
  presets = [],
  value,
  onSelect,
  openEnd,
}: {
  presets?: DateRangePreset[];
  value: IsoDateRange;
  onSelect: (preset: DateRangePreset) => void;
  openEnd?: { pressed: boolean; onSelect: () => void };
}) {
  const { t } = useTranslation('dashboard', { keyPrefix: 'global' });
  return (
    <Column role='group' aria-label={String(t('calendar_presets'))} data-testid='calendar-presets'>
      {openEnd && (
        <PresetButton
          disableRipple
          aria-pressed={openEnd.pressed}
          onClick={openEnd.onSelect}
          data-testid='calendar-open-end'
        >
          {t('calendar_open_end')}
        </PresetButton>
      )}
      {openEnd && presets.length > 0 && <Divider aria-hidden />}
      {presets.map((preset) => (
        <PresetButton
          key={preset.key}
          disableRipple
          aria-pressed={!openEnd?.pressed && rangesEqual(preset.value, value)}
          onClick={() => onSelect(preset)}
          data-testid={`calendar-preset-${preset.key}`}
        >
          {preset.label}
        </PresetButton>
      ))}
    </Column>
  );
}
